import {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createStrictOptionalContext } from 'helpers/context';
import { HashMap } from 'shared/types/commonView';
import { isBlobUrl, revokeBlobUrls } from 'shared/utils/blobUrl';

interface BlobFileStorageContext {
  setItem: (key: string, value: string) => void;
  getItem: (key: string) => string | null;
  removeItem: (key: string) => void;
  clear: () => void;
}

const { Provider: BlobFileStorageProvider, useRequiredContext: useBlobFileStorage } =
  createStrictOptionalContext<BlobFileStorageContext>(
    'useBlobFileStorage',
    'WithBlobFileStorage'
  );

function WithBlobFileStorage({
  children,
}: PropsWithChildren): ReactElement {
  const [, setBlobs] = useState<HashMap<string>>({});
  const blobsRef = useRef<HashMap<string>>({});

  useEffect(() => {
    return () => {
      revokeBlobUrls(
        Object.values(blobsRef.current).filter((value): value is string =>
          isBlobUrl(value)
        )
      );
    };
  }, []);

  const setItem = useCallback((key: string, value: string) => {
    const previousValue = blobsRef.current[key];
    const next = { ...blobsRef.current, [key]: value };
    blobsRef.current = next;
    setBlobs(next);

    if (
      previousValue &&
      previousValue !== value &&
      isBlobUrl(previousValue)
    ) {
      revokeBlobUrls([previousValue]);
    }
  }, []);
  const getItem = useCallback(
    (key: string) => {
      return blobsRef.current[key] || null;
    },
    []
  );

  const removeItem = useCallback((key: string) => {
    const removedValue = blobsRef.current[key];
    const next = { ...blobsRef.current };
    delete next[key];
    blobsRef.current = next;
    setBlobs(next);

    if (isBlobUrl(removedValue)) {
      revokeBlobUrls([removedValue]);
    }
  }, []);

  const clear = useCallback(() => {
    const values = Object.values(blobsRef.current).filter(
      (value): value is string => isBlobUrl(value)
    );
    blobsRef.current = {};
    setBlobs({});
    revokeBlobUrls(values);
  }, []);

  const ctx = {
    setItem,
    getItem,
    removeItem,
    clear,
  };

  return (
    <BlobFileStorageProvider value={ctx}>
      {children}
    </BlobFileStorageProvider>
  );
}

export { WithBlobFileStorage, useBlobFileStorage };
